import { Subscription, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
    FormsModule,
    ReactiveFormsModule,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    Validators
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { unauthorizedContentRedirectKey } from '@core/authentication/redirect-util';
import { BasketService } from '@core/services/basket/basket.service';
import { NotificationService } from '@core/services/notification/notification.service';
import { SettingsService } from '@core/services/settings.service';
import { StorageService } from '@core/services/storage.service';
import { LoginPanelComponent } from '@features/login-panel/login-panel/login-panel.component';
import { ProductDetailService } from '@features/product-detail/product-detail.service';
import { StoreStockContainerComponent } from '@features/store-stock/store-stock-container/store-stock-container.component';
import { StoresService } from '@features/stores-overview/service/stores.service';
import { OverlayService } from '@libs/overlay';
import { ProductDetailViewModel, RelatedProperties } from '@models/api-search.models';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FormModelControls } from '@shared/form-elements/model/form-model-controls';

import { getPerfectVariantMatch, getVariantMatch } from './product-details.helpers';
import { AddToBasketState } from '@features/product/product';
import { CanChangeStoreInReservationPipe } from '@features/reserve-in-store/reserve-in-store-pipes/can-change-store-in-reservation.pipe';
import { StoreStockStatusTranslationPipe } from '@features/store-stock/store-stock-pipes/store-stock-status-translation.pipe';
import { StoreStockStatusPipe } from '@features/store-stock/store-stock-pipes/store-stock-status.pipe';
import { StoresWithStock } from '@features/store-stock/store-stock-pipes/stores-with-stock.pipe';
import { StoreMatches } from '@features/store-stock/store-stock-pipes/store-matches.pipe';
import { StoreStock } from '@features/store-stock/store-stock-pipes/store-stock.pipe';
import { HasRelatedPropertiesPipe } from '@features/product/pipes/has-related-properties.pipe';
import { TranslateSafePipe } from '@shared/translate-safe/translate-safe.pipe';
import { FormatStringPipe } from '@shared/format-string/format-string.pipe';
import { ProductGiftOptionsComponent } from '@features/product/product-gift-options/product-gift-options.component';
import { TooltipComponent } from '@shared/tooltip/tooltip.component';
import { StockStatusComponent } from '@shared/stock-status/stock-status.component';
import { StoreStockDetailedComponent } from '@features/store-stock/store-stock-detailed/store-stock-detailed.component';
import { SvgIconComponent } from '@shared/svg-icon/svg-icon.component';
import { ProductActionsComponent } from '@features/product/product-actions/product-actions.component';
import { QuantityComponent } from '@shared/form-elements/quantity/quantity.component';
import { ProductPriceComponent } from '@features/product/product-price/product-price.component';
import { ProductRelatedPropertiesComponent } from '@features/product-detail/product-related-properties/product-related-properties.component';
import { BrandLinkComponent } from '@shared/brand-link/brand-link.component';
import { AsyncPipe, NgClass, NgIf } from '@angular/common';
import { PaymentMethodsComponent } from '@shared/payment-methods/payment-methods.component';

@UntilDestroy()
@Component({
    selector: 'app-product-details',
    templateUrl: './product-details.component.html',
    styleUrls: ['./product-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgIf,
        NgClass,
        BrandLinkComponent,
        FormsModule,
        ProductRelatedPropertiesComponent,
        ReactiveFormsModule,
        ProductPriceComponent,
        QuantityComponent,
        ProductActionsComponent,
        SvgIconComponent,
        StoreStockDetailedComponent,
        StockStatusComponent,
        TooltipComponent,
        ProductGiftOptionsComponent,
        AsyncPipe,
        FormatStringPipe,
        TranslateSafePipe,
        HasRelatedPropertiesPipe,
        StoreStock,
        StoreMatches,
        StoresWithStock,
        StoreStockStatusPipe,
        StoreStockStatusTranslationPipe,
        CanChangeStoreInReservationPipe,
        PaymentMethodsComponent
    ]
})
export class ProductDetailsComponent implements OnInit {
    private formSub: Subscription;
    public form: UntypedFormGroup;
    public quantity: UntypedFormControl = new UntypedFormControl(1);

    @Input() allowGiftWrapping?: boolean;

    @Output()
    showMoreClicked: EventEmitter<void> = new EventEmitter();

    constructor(
        private readonly notificationService: NotificationService,
        private readonly overlayService: OverlayService,
        private readonly storageService: StorageService,
        private readonly route: ActivatedRoute,
        public readonly settingsService: SettingsService,
        public readonly basketService: BasketService,
        public readonly formBuilder: UntypedFormBuilder,
        public readonly productDetailService: ProductDetailService,
        public readonly storesService: StoresService,
        public readonly authenticationService: AuthenticationService
    ) {}

    ngOnInit(): void {
        this.productDetailService.productDetail$
            .pipe(
                untilDestroyed(this),
                catchError(error => throwError(error))
            )
            .subscribe(
                product => {
                    if (product) {
                        this.createForm(product);
                    }
                },
                error => {
                    this.notificationService.createNotification({
                        type: 'error',
                        translationHeadline: 'productDetail.feedback.variantChangeError',
                        ...(error.message ? { messageText: error.message } : {})
                    });
                }
            );
    }

    public showLoginPrompt(): void {
        const returnPage = this.route.snapshot.url[0].path;
        this.storageService.set(unauthorizedContentRedirectKey, returnPage);
        this.overlayService.open(LoginPanelComponent);
    }

    public openStockOverlay(): void {
        this.overlayService.open(StoreStockContainerComponent, {
            data: {
                stockSidebarType: 'stock-status'
            }
        });
    }

    public onStatusEvent(status: AddToBasketState): void {
        if (status === 'success') {
            this.quantity.patchValue(1);
        }
    }

    private createForm(product: ProductDetailViewModel): void {
        if (!product.relatedProperties) {
            return;
        }
        // Create form controls. E.g. color, width, etc. selection groups
        const formControls: FormModelControls<RelatedProperties> = Object.fromEntries(
            Object.entries(product.relatedProperties).map(([propKey, propValue]) => {
                if (propKey in product) {
                    // If value is an object, we use the 'key' property of the value
                    const value = product[propKey as keyof RelatedProperties];
                    const initialValue = typeof value === 'object' && value !== null ? value.key : value;
                    return [propKey, [initialValue, Validators.required]];
                } else {
                    return [propKey, [propValue && propValue.length ? propValue[0].value : '', Validators.required]];
                }
            })
        );
        this.form = this.formBuilder.group(formControls);
        this.formSub?.unsubscribe();
        Object.keys(formControls).forEach(key => {
            const variants = product.variantProducts?.items;
            if (!variants) {
                return;
            }
            this.form.controls[key].valueChanges.pipe(untilDestroyed(this)).subscribe(selectedPropertyValue => {
                // When selecting another variant, look for a variant that matches on all selected properties, e.g. diameter AND color
                const perfectMatch = getPerfectVariantMatch(variants, this.form.getRawValue() as RelatedProperties);
                if (perfectMatch) {
                    this.productDetailService.requestProduct(perfectMatch.productNo);
                    return;
                }
                // If we could not find a perfect match, we find the first variant that just matches the property on which we clicked
                const partialMatch = variants.find(variant =>
                    getVariantMatch(variant, key as keyof RelatedProperties, selectedPropertyValue)
                );
                if (partialMatch) {
                    this.productDetailService.requestProduct(partialMatch.productNo);
                    this.notificationService.createNotification({
                        type: 'info',
                        translationHeadline: 'productDetail.feedback.partialMatchHeadline',
                        translationText: 'productDetail.feedback.partialMatchText',
                        fadeTime: 3000
                    });
                    return;
                }
                // If there is no match at all (unlikely), we show error message.
                this.notificationService.createNotification({
                    type: 'error',
                    translationHeadline: 'productDetail.feedback.variantChangeError'
                });
            });
        });
    }
}
